import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Card from '../../components/ui/Card';
import Pill from '../../components/ui/Pill';
import Button from '../../components/ui/Button';
import EmptyState from '../../components/ui/EmptyState';
import StatusBanner from '../../components/ui/StatusBanner';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import { useEmployeeDocuments } from '../../hooks/useEmployeeDocuments';
import { useEmployeeDocumentMutations } from '../../hooks/useEmployeeDocumentMutations';
import EmployeeDocumentUploadModal from './EmployeeDocumentUploadModal';
import EditDocumentExpirationModal from './EditDocumentExpirationModal';
import Checkbox from '../../components/ui/Checkbox';
import { CERTIFICATION_STATUS, classifyCertification } from './certificationStatus';

const DOCUMENT_TYPE_LABELS = {
  offer_letter: 'Offer letter',
  certification: 'Certification',
  other: 'Approved general employment document'
};

const TYPE_ORDER = ['offer_letter', 'certification', 'other'];

function formatBytes(n) {
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n > 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.ceil(n / 1024)} KB`;
}
function formatDate(iso) {
  if (!iso) return '—';
  return String(iso).slice(0, 10);
}

function ExpirationPill({ doc }) {
  if (!doc.expiresAt) return null;
  const status = classifyCertification(doc);
  const dateStr = formatDate(doc.expiresAt);
  if (status === CERTIFICATION_STATUS.EXPIRED) {
    return <Pill variant="danger" data-testid="document-expired-pill">Expired {dateStr}</Pill>;
  }
  if (status === CERTIFICATION_STATUS.EXPIRING) {
    return <Pill variant="warning" data-testid="document-expiring-pill">Expires {dateStr}</Pill>;
  }
  return <Pill variant="neutral" data-testid="document-ok-pill">Expires {dateStr}</Pill>;
}

function DocumentRow({ doc, canArchive, canEditExpiration, onOpen, onArchive, onEditExpiration, busy }) {
  const isArchived = Boolean(doc.archivedAt);
  // "Edit expiration" affordance shows only on active certification
  // rows when the caller can write — matches the upload modal scope.
  const showEditExpiration = canEditExpiration
    && !isArchived
    && doc.documentType === 'certification';
  const editLabel = doc.expiresAt ? 'Edit expiration' : 'Set expiration';
  return (
    <div className="employee-documents__row">
      <div className="employee-documents__row-main">
        <div className="employee-documents__row-title">
          <span>{DOCUMENT_TYPE_LABELS[doc.documentType] || doc.documentType}</span>
          {isArchived ? <Pill variant="warning">Archived</Pill> : null}
          <ExpirationPill doc={doc} />
        </div>
        <div className="employee-documents__row-meta">
          {doc.mimeType} · {formatBytes(doc.sizeBytes)} · uploaded {formatDate(doc.uploadedAt)}
        </div>
      </div>
      <div className="employee-documents__row-actions">
        <Button size="sm" variant="secondary" onClick={() => onOpen(doc)} disabled={busy}>Open</Button>
        {showEditExpiration ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onEditExpiration(doc)}
            disabled={busy}
            data-testid={`document-edit-expiration-${doc.id}`}
          >
            {editLabel}
          </Button>
        ) : null}
        {canArchive && !isArchived ? (
          <Button size="sm" variant="secondary" onClick={() => onArchive(doc)} disabled={busy}>Archive</Button>
        ) : null}
      </div>
    </div>
  );
}

function DocumentsByType({ docs, canArchive, canEditExpiration, onOpen, onArchive, onEditExpiration, busy }) {
  const byType = useMemo(() => {
    const out = {};
    for (const t of TYPE_ORDER) out[t] = [];
    for (const doc of docs) {
      const key = TYPE_ORDER.includes(doc.documentType) ? doc.documentType : 'other';
      out[key].push(doc);
    }
    return out;
  }, [docs]);

  return (
    <div className="employee-documents__groups">
      {TYPE_ORDER.map((t) => byType[t].length ? (
        <div key={t} className="employee-documents__group">
          <h3 className="employee-documents__group-title">{DOCUMENT_TYPE_LABELS[t]}</h3>
          <div className="employee-documents__group-rows">
            {byType[t].map(doc => (
              <DocumentRow
                key={doc.id}
                doc={doc}
                canArchive={canArchive}
                canEditExpiration={canEditExpiration}
                onOpen={onOpen}
                onArchive={onArchive}
                onEditExpiration={onEditExpiration}
                busy={busy}
              />
            ))}
          </div>
        </div>
      ) : null)}
    </div>
  );
}

export default function EmployeeDocumentsPanel({
  profileId,
  canReadDocuments,
  canWriteDocuments,
  defaultDocumentType = 'other',
  uploadRequest = 0,
  onDocumentsChanged = null,
  // W2: when set, filters visible documents to a single type and
  // re-skins the empty state / upload modal accordingly. Used by the
  // new Certifications tab without forking the panel.
  documentTypeFilter = null,
  title = null,
  emptyHeadline = null,
  emptyBody = null
}) {
  const confirm = useConfirm();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [expirationDoc, setExpirationDoc] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [banner, setBanner] = useState('');
  const [actionError, setActionError] = useState('');

  const enabled = canReadDocuments && profileId;
  const { documents, status, error, refetch } = useEmployeeDocuments(
    enabled ? profileId : null,
    { includeArchived }
  );
  const { uploadDocument, archiveDocument, getSignedUrl, setExpiration } = useEmployeeDocumentMutations({ profileId });

  useEffect(() => {
    if (uploadRequest && canWriteDocuments) {
      setUploadOpen(true);
    }
  }, [uploadRequest, canWriteDocuments]);

  const handleOpen = useCallback(async (doc) => {
    setActionBusy(true);
    setActionError('');
    try {
      const result = await getSignedUrl(doc.id);
      const url = result?.url;
      if (!url) throw new Error('No URL returned for this document.');
      if (typeof window !== 'undefined' && window.open) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      setActionError(err?.message || 'Unable to open document.');
    } finally {
      setActionBusy(false);
    }
  }, [getSignedUrl]);

  const handleArchive = useCallback(async (doc) => {
    const ok = await confirm(
      `Archive this ${DOCUMENT_TYPE_LABELS[doc.documentType] || doc.documentType}? It stays queryable for compliance.`,
      { confirmLabel: 'Archive document', tone: 'warning' }
    );
    if (!ok) return;
    setActionBusy(true);
    setActionError('');
    try {
      await archiveDocument(doc.id);
      setBanner('Document archived.');
      await refetch();
      await onDocumentsChanged?.();
    } catch (err) {
      setActionError(err?.message || 'Unable to archive document.');
    } finally {
      setActionBusy(false);
    }
  }, [confirm, archiveDocument, refetch, onDocumentsChanged]);

  const handleSubmitExpiration = useCallback(async (payload) => {
    if (!expirationDoc) return;
    await setExpiration(expirationDoc.id, payload);
    setBanner(payload.clear ? 'Expiration cleared.' : 'Expiration updated.');
    await refetch();
    await onDocumentsChanged?.();
  }, [expirationDoc, setExpiration, refetch, onDocumentsChanged]);

  // ── Render branches ───────────────────────────────────────────

  // W2: apply the optional document type filter (client-side) so the
  // panel can be reused inside the Certifications tab without forking.
  const visibleDocuments = documentTypeFilter
    ? documents.filter(doc => doc.documentType === documentTypeFilter)
    : documents;
  const panelTitle = title || 'Documents';
  const panelEmptyHeadline = emptyHeadline
    || (documentTypeFilter === 'certification' ? 'No certifications on file' : 'No documents on file');
  const panelEmptyBody = emptyBody
    || (documentTypeFilter === 'certification'
      ? (canWriteDocuments ? 'Upload certifications with expiration dates to drive the renewal queue + alerts.' : 'No certifications have been uploaded for this employee yet.')
      : (canWriteDocuments ? 'Upload an offer letter, certification, or approved low-sensitivity employment document.' : 'No documents have been uploaded for this employee yet.'));

  if (!canReadDocuments || status === 'forbidden') {
    return (
      <Card className="employee-documents" padding="md" shadow="sm">
        <h2 className="employee-documents__title">{panelTitle}</h2>
        <EmptyState
          compact
          icon="·"
          headline="Restricted"
          body="Employee documents are visible only to users with the appropriate permission."
        />
      </Card>
    );
  }

  return (
    <Card className="employee-documents" padding="md" shadow="sm" data-testid="employee-documents-panel">
      <div className="employee-documents__header">
        <h2 className="employee-documents__title">{panelTitle}</h2>
        <div className="employee-documents__header-actions">
          <Checkbox
            className="employee-documents__toggle"
            label="Show archived"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
            data-testid="employee-documents-include-archived"
          />
          {canWriteDocuments ? (
            <Button
              size="sm"
              variant="primary"
              onClick={() => setUploadOpen(true)}
              disabled={actionBusy}
              data-testid={documentTypeFilter === 'certification' ? 'employee-certifications-upload' : 'employee-documents-upload'}
            >
              {documentTypeFilter === 'certification' ? 'Upload certification' : 'Upload document'}
            </Button>
          ) : null}
        </div>
      </div>

      {banner ? <StatusBanner kind="success" message={banner} live /> : null}
      {actionError ? <StatusBanner kind="error" message={actionError} /> : null}
      {status === 'error' ? (
        <StatusBanner kind="error" message={error} onRetry={refetch} />
      ) : null}

      {status === 'loading' && visibleDocuments.length === 0 ? (
        <LoadingSkeleton variant="paragraph" lines={4} />
      ) : visibleDocuments.length === 0 ? (
        <EmptyState
          compact
          icon="·"
          headline={panelEmptyHeadline}
          body={panelEmptyBody}
        />
      ) : (
        <DocumentsByType
          docs={visibleDocuments}
          canArchive={canWriteDocuments}
          canEditExpiration={canWriteDocuments}
          onOpen={handleOpen}
          onArchive={handleArchive}
          onEditExpiration={setExpirationDoc}
          busy={actionBusy}
        />
      )}

      <EmployeeDocumentUploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        defaultDocumentType={defaultDocumentType}
        uploader={uploadDocument}
        onUploaded={async () => {
          setBanner('Document uploaded.');
          await refetch();
          await onDocumentsChanged?.();
        }}
        presentation="drawer"
      />
      <EditDocumentExpirationModal
        open={Boolean(expirationDoc)}
        onClose={() => setExpirationDoc(null)}
        doc={expirationDoc}
        onSubmit={handleSubmitExpiration}
      />
    </Card>
  );
}
